<div class="border-b border-slate-200 px-5 py-4">
  <div class="flex items-start justify-between gap-3">
    <div>
      <h4 class="text-lg font-semibold text-slate-900">
        SUNAT · Reconsultar pendientes
      </h4>
      <p class="mt-1 text-sm text-slate-500">
        Consulta a SUNAT el estado de las ventas pendientes de una empresa y
        actualiza su resultado en el sistema.
      </p>
    </div>
    <button
      type="button"
      class="rounded-lg border border-slate-300 px-2 py-1 text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
      aria-label="Cerrar"
      [disabled]="ejecutando"
      (click)="cerrar()"
    >
      <i class="bi bi-x"></i>
    </button>
  </div>
</div>

<div class="space-y-4 px-5 py-4">
  <div class="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
    <div class="flex flex-wrap items-end justify-between gap-2">
      <div class="w-full sm:w-auto sm:flex-1">
        <label
          for="sunat-empresa"
          class="mb-1 block text-xs font-medium text-slate-600"
        >
          Empresa
        </label>

        @if (empresas().length > 1) {
          <select
            id="sunat-empresa"
            data-testid="sunat-empresa"
            class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200 disabled:bg-slate-100"
            [(ngModel)]="empresaId"
            [disabled]="ejecutando"
            (ngModelChange)="onEmpresaChange()"
          >
            @for (item of empresas(); track item.id) {
              <option [ngValue]="item.id">
                {{ item.nombre }} — {{ item.ruc }}
              </option>
            }
          </select>
          <p class="mt-1 text-xs text-slate-500">
            RUC {{ empresaActual?.ruc || '—' }} · Empresa #{{
              empresaActual?.id ?? '—'
            }}
          </p>
        } @else {
          <p class="text-sm font-semibold text-slate-900">
            {{ empresaActual?.nombre || 'Empresa sin nombre' }}
          </p>
          <p class="text-xs text-slate-500">
            RUC {{ empresaActual?.ruc || '—' }} · Empresa #{{
              empresaActual?.id ?? '—'
            }}
          </p>
        }
      </div>
      <span
        class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
        [class.bg-emerald-100]="!empresaSinToken"
        [class.text-emerald-700]="!empresaSinToken"
        [class.bg-amber-100]="empresaSinToken"
        [class.text-amber-800]="empresaSinToken"
      >
        <i
          class="bi"
          [class.bi-check-circle]="!empresaSinToken"
          [class.bi-exclamation-triangle]="empresaSinToken"
        ></i>
        {{ empresaSinToken ? 'Sin token SUNAT' : 'Token SUNAT configurado' }}
      </span>
    </div>
  </div>

  @if (empresaSinToken) {
    <div
      class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
      role="alert"
    >
      <i class="bi bi-exclamation-triangle mr-1"></i>
      Esta empresa no tiene token SUNAT. Las ventas se contabilizarán como
      <strong>sin token</strong> hasta que se configure.
    </div>
  }

  <div>
    <label for="sunat-limit" class="mb-1 block text-sm font-medium text-slate-700">
      Límite de ventas a procesar
    </label>
    <input
      id="sunat-limit"
      type="number"
      class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200 disabled:bg-slate-100"
      [min]="limitMin"
      [max]="limitMax"
      step="1"
      [(ngModel)]="limit"
      [disabled]="ejecutando"
    />
    <p class="mt-1 text-xs text-slate-500">
      Entre {{ limitMin }} y {{ limitMax }} ventas (por defecto 200). El backend
      procesa las más antiguas primero.
    </p>
    @if (limitInvalido) {
      <p class="mt-1 text-xs text-red-600">
        Ingresa un número entero entre {{ limitMin }} y {{ limitMax }}.
      </p>
    }
  </div>

  <div class="rounded-lg border border-sky-200 bg-sky-50/70 px-3 py-2 text-xs text-slate-600">
    La consulta se hace venta por venta y puede tardar varios minutos si el
    límite es alto. Mientras corre, no cierres esta ventana.
  </div>

  @if (confirmando) {
    <div
      class="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
      role="alertdialog"
      aria-label="Confirmar reconsulta SUNAT"
    >
      <p class="text-sm font-medium text-amber-900">
        ¿Confirmas la reconsulta para
        {{ empresaActual?.nombre || 'esta empresa' }}?
      </p>
      <p class="mt-1 text-xs text-amber-800">
        Se consultarán hasta {{ limit }} ventas pendientes y se registrarán los
        cambios en SUNAT.
      </p>
      <div class="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          class="rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="sunat-confirmar"
          [disabled]="ejecutando"
          (click)="ejecutarReconsulta()"
        >
          Sí, reconsultar
        </button>
        <button
          type="button"
          class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          [disabled]="ejecutando"
          (click)="cancelarConfirmacion()"
        >
          Cancelar
        </button>
      </div>
    </div>
  }

  @if (ejecutando) {
    <div
      class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600"
      data-testid="sunat-ejecutando"
      aria-live="polite"
    >
      <span
        class="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-sky-600"
      ></span>
      Reconsultando SUNAT... esto puede tardar unos minutos.
    </div>
  }

  @if (error) {
    <div
      class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
      data-testid="sunat-error"
      role="alert"
      aria-live="assertive"
    >
      <div class="flex items-start gap-2">
        <i class="bi bi-exclamation-octagon mt-0.5"></i>
        <div>
          <p class="font-medium">No se pudo completar la reconsulta</p>
          <p class="mt-1">{{ error.message }}</p>
          @if (error.status > 0) {
            <p class="mt-1 text-xs text-red-700">
              Código HTTP: {{ error.status }}
            </p>
          }
        </div>
      </div>
    </div>
  }

  @if (resumen) {
    <div
      class="rounded-xl border border-slate-200 bg-white px-4 py-3"
      data-testid="sunat-resumen"
      aria-live="polite"
    >
      <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h5 class="text-sm font-semibold text-slate-900">Resumen de la ejecución</h5>
        <span class="text-xs text-slate-500">
          Origen: {{ resumen.origen }} · Empresa #{{ resumen.empresa_id }} ·
          Límite {{ resumen.limit }}
        </span>
      </div>

      <dl class="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <div class="rounded-lg bg-slate-50 px-3 py-2">
          <dt class="text-xs text-slate-500">Procesadas</dt>
          <dd class="text-lg font-semibold text-slate-900">
            {{ resumen.procesadas }}
          </dd>
        </div>
        <div class="rounded-lg bg-emerald-50 px-3 py-2">
          <dt class="text-xs text-emerald-700">Actualizadas</dt>
          <dd class="text-lg font-semibold text-emerald-800">
            {{ resumen.actualizadas }}
          </dd>
        </div>
        <div class="rounded-lg bg-slate-50 px-3 py-2">
          <dt class="text-xs text-slate-500">Sin cambios</dt>
          <dd class="text-lg font-semibold text-slate-900">
            {{ resumen.sin_cambios }}
          </dd>
        </div>
        <div class="rounded-lg bg-amber-50 px-3 py-2">
          <dt class="text-xs text-amber-700">Sin token</dt>
          <dd class="text-lg font-semibold text-amber-800">
            {{ resumen.sin_token }}
          </dd>
        </div>
        <div
          class="rounded-lg px-3 py-2"
          [class.bg-red-50]="resumen.errores > 0"
          [class.bg-slate-50]="resumen.errores === 0"
        >
          <dt class="text-xs" [class.text-red-700]="resumen.errores > 0" [class.text-slate-500]="resumen.errores === 0">
            Errores
          </dt>
          <dd
            class="text-lg font-semibold"
            [class.text-red-800]="resumen.errores > 0"
            [class.text-slate-900]="resumen.errores === 0"
          >
            {{ resumen.errores }}
          </dd>
        </div>
        <div class="rounded-lg bg-slate-50 px-3 py-2">
          <dt class="text-xs text-slate-500">Duración</dt>
          <dd class="text-lg font-semibold text-slate-900">
            {{ duracionSegundos }} s
          </dd>
        </div>
      </dl>

      @if (resumen.en_curso) {
        <p class="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <i class="bi bi-hourglass-split mr-1"></i>
          Ya había una reconsulta en curso; el backend no ejecutó esta solicitud.
        </p>
      } @else if (bloqueoPresunto) {
        <p class="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <i class="bi bi-info-circle mr-1"></i>
          Todos los contadores están en cero. Puede que no hubiera ventas
          pendientes o que ya existiera una reconsulta en curso (el backend hoy
          no distingue ambos casos).
        </p>
      }
    </div>
  }
</div>

<div
  class="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-5 py-4"
>
  <span class="text-xs text-slate-500">
    Se registrará como ejecutado por
    <strong class="text-slate-700">{{ ejecutadoPor }}</strong>.
  </span>
  <div class="flex flex-wrap gap-2">
    <button
      type="button"
      class="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-60"
      data-testid="sunat-solicitar"
      [disabled]="ejecutando || confirmando || limitInvalido"
      (click)="solicitarConfirmacion()"
    >
      @if (ejecutando) {
        <span
          class="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
        ></span>
        Reconsultando...
      } @else if (resumen) {
        <i class="bi bi-arrow-repeat"></i>
        Reconsultar de nuevo
      } @else {
        <i class="bi bi-cloud-arrow-down"></i>
        Reconsultar pendientes
      }
    </button>
    <button
      type="button"
      class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
      [disabled]="ejecutando"
      (click)="cerrar()"
    >
      Cerrar
    </button>
  </div>
</div>
